/**
 * LaneProgress: where a one-attempt lane run stands, as one short row in the
 * planning ladder's markup.
 *
 * The lane's session reports its pipeline stages and its tool calls, and
 * nothing between them: the legacy planner's milestone beats never come, so
 * the planning ladder sat on its first rung for a whole plan session and read
 * as stuck (NEARM-523 on staging, 2026-10-24). This row draws the stages the
 * session reported, in the order it started them. The one it is on counts its
 * time and its tool calls; a plan stage that finished is Ready. A stage no
 * session reported is drawn only as pending, and one no session finished is
 * never drawn as done.
 *
 * The run carries every session of its attempt. Only the current session is
 * "now": a stage an earlier session started and never finished (a session
 * that died) waits as pending until the current one reaches it.
 */
import type { ReactNode } from "react";
import type { CodingEvent, WorkItemRunListEntry } from "@yagni/shared";
import { sinceLabel } from "./LiveStream.js";
import type { LadderRungState } from "./PlanningLadder.js";

export type LaneRungId = "map" | "plan" | "implement" | "review" | "fix" | "ready";

export interface LaneRung {
  id: LaneRungId;
  word: string;
  state: LadderRungState;
  /** The small words under the rung: its time and calls, "stopped here". Null for none. */
  sub: string | null;
}

type LaneStage = Exclude<LaneRungId, "ready">;

type StageEvent = Extract<CodingEvent, { kind: "stage" }>;

const STAGE_WORD: Record<LaneStage, string> = {
  map: "Read",
  plan: "Plan",
  implement: "Build",
  review: "Review",
  fix: "Fix",
};

const LANE_STAGES: ReadonlySet<string> = new Set(Object.keys(STAGE_WORD));

function isLaneStage(stage: unknown): stage is LaneStage {
  return typeof stage === "string" && LANE_STAGES.has(stage);
}

/**
 * How far before the session's recorded start its own events may be dated.
 * The session's stage lines carry the sandbox's clock, the record the host's.
 */
const SESSION_CLOCK_SLACK_MS = 10_000;

/** When a lane run's current session began; null for every other run. */
export function laneSessionStartOf(
  run: Pick<WorkItemRunListEntry, "lane" | "sessionStartedAt"> | null | undefined,
): string | null {
  return run?.lane === "attempt" ? (run.sessionStartedAt ?? null) : null;
}

/**
 * Which of a lane run's sessions an event belongs to: the newest one started
 * by the event's time, allowing for the two clocks. Null before the first, or
 * when the run names no sessions.
 */
export function sessionAtOf(
  sessions: WorkItemRunListEntry["sessions"],
  at: number,
): NonNullable<WorkItemRunListEntry["sessions"]>[number] | null {
  let found: NonNullable<WorkItemRunListEntry["sessions"]>[number] | null = null;
  for (const session of sessions ?? []) {
    const started = Date.parse(session.startedAt);
    if (Number.isFinite(started) && started - SESSION_CLOCK_SLACK_MS <= at) found = session;
  }
  return found;
}

/** The events of the session that began at `sessionStartMs`; all of them when it is unknown. */
export function sessionEventsOf(
  events: readonly CodingEvent[],
  sessionStartMs: number | null,
): readonly CodingEvent[] {
  if (sessionStartMs == null || !Number.isFinite(sessionStartMs)) return events;
  const from = sessionStartMs - SESSION_CLOCK_SLACK_MS;
  return events.filter((event) => event.at >= from);
}

function callsWord(count: number): string {
  return `${count} call${count === 1 ? "" : "s"}`;
}

/**
 * The row as of `nowMs`. `purpose` is what the run is for now (the runs list
 * reads a planning attempt as a plan); `sessionStartMs` is when its current
 * session began. Once the run is not live, nothing is "now": the stage it
 * ended on says stopped, and a finished plan is Ready.
 *
 * A fix session (`sessionStage: "fix"`, YAG-1079) reads its pull request and
 * then writes the fix, and reports those as Read and Build: drawn as they
 * came, the strip lit Build again for every fix round (ENT-1100, 2026-10-28).
 * Every stage the fix session reports is its Fix rung.
 */
export function laneProgressOf(input: {
  events: readonly CodingEvent[];
  purpose: WorkItemRunListEntry["purpose"] | null | undefined;
  sessionStartMs: number | null;
  nowMs: number;
  live: boolean;
  sessionStage?: WorkItemRunListEntry["sessionStage"] | null;
}): LaneRung[] {
  const { events, purpose, sessionStartMs, nowMs, live } = input;
  const fixing = input.sessionStage === "fix";
  const session = sessionEventsOf(events, sessionStartMs);
  const inSession = new Set(sessionStartMs == null ? [] : session);
  const stageOf = (event: StageEvent): LaneStage | null => {
    if (!isLaneStage(event.stage)) return null;
    return fixing && inSession.has(event) && event.stage !== "review" ? "fix" : event.stage;
  };
  const order: LaneStage[] = [];
  const startedAt = new Map<LaneStage, number>();
  const finished = new Set<LaneStage>();
  let last: LaneStage | null = null;
  for (const event of events) {
    if (event.kind !== "stage") continue;
    const stage = stageOf(event);
    if (!stage) continue;
    if (!startedAt.has(stage)) order.push(stage);
    if (event.phase === "start") {
      // The fix session's write follows its read: the Fix rung counts from the read.
      const sameFix =
        stage === "fix" && last === "fix" && !finished.has("fix") && inSession.has(event);
      if (!sameFix || !startedAt.has(stage)) startedAt.set(stage, event.at);
      finished.delete(stage);
      last = stage;
    } else {
      startedAt.set(stage, startedAt.get(stage) ?? event.at);
      // A fix session's read finishing is not its fix finishing.
      if (!(fixing && stage === "fix" && event.stage === "map")) finished.add(stage);
    }
  }

  // The stage the current session is on: the newest one started, unfinished,
  // and started by this session rather than an earlier one that died.
  const current =
    live &&
    last &&
    !finished.has(last) &&
    session.some(
      (event) => event.kind === "stage" && event.phase === "start" && stageOf(event) === last,
    )
      ? last
      : null;
  const nowSub = (since: number) => {
    const calls = session.filter((event) => event.kind === "tool_call" && event.at >= since).length;
    const time = sinceLabel(since, nowMs);
    return calls > 0 ? `${time} · ${callsWord(calls)}` : time;
  };

  const rungs: LaneRung[] = order.map((id) => {
    const base = { id, word: STAGE_WORD[id] };
    if (finished.has(id)) return { ...base, state: "done", sub: null };
    if (id === current) return { ...base, state: "now", sub: nowSub(startedAt.get(id) ?? nowMs) };
    if (!live && id === last) return { ...base, state: "stopped", sub: "stopped here" };
    return { ...base, state: "pending", sub: null };
  });

  // What the run is for, while no session has reported that stage: pending,
  // or now when this session is making calls before its first stage line.
  const planning = purpose === "planning";
  const purposeStage: LaneStage = planning ? "plan" : fixing ? "fix" : "implement";
  if (live && !order.includes(purposeStage)) {
    const firstCall = session.find((event) => event.kind === "tool_call");
    const working = current == null && firstCall != null;
    rungs.push({
      id: purposeStage,
      word: STAGE_WORD[purposeStage],
      state: working ? "now" : "pending",
      sub: working ? nowSub(Math.min(sessionStartMs ?? firstCall.at, firstCall.at)) : null,
    });
  }
  if (planning) {
    rungs.push({
      id: "ready",
      word: "Ready",
      state: finished.has("plan") ? "done" : "pending",
      sub: null,
    });
  }
  return rungs;
}

export interface LaneProgressProps {
  /** The run on the card. The row draws only for a one-attempt lane run. */
  run: Pick<WorkItemRunListEntry, "lane" | "purpose" | "sessionStartedAt" | "sessionStage"> | null;
  events: readonly CodingEvent[];
  live: boolean;
  /** The clock the current rung counts against: now while live, the end once it stopped. */
  nowMs: number;
}

export function LaneProgress({ run, events, live, nowMs }: LaneProgressProps): ReactNode {
  if (run?.lane !== "attempt") return null;
  // An ended run that reported no stage has no row to draw.
  if (!live && !events.some((event) => event.kind === "stage" && isLaneStage(event.stage))) {
    return null;
  }
  const session = laneSessionStartOf(run);
  const rungs = laneProgressOf({
    events,
    purpose: run.purpose,
    sessionStartMs: session ? Date.parse(session) : null,
    nowMs,
    live,
    sessionStage: run.sessionStage ?? null,
  });
  return (
    <ol className="ls-ladder" data-testid="lane-progress" aria-label="Session steps">
      {rungs.map((rung) => (
        <li
          key={rung.id}
          className={`ls-rung is-${rung.state}`}
          data-rung={rung.id}
          data-state={rung.state}
          aria-current={rung.state === "now" ? "step" : undefined}
        >
          <span className="ls-rung-dot" aria-hidden />
          <span className="ls-rung-word">{rung.word}</span>
          {rung.sub && <span className="ls-rung-sub">{rung.sub}</span>}
        </li>
      ))}
    </ol>
  );
}
